// src/components/AgentPanel.js
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// API functions for services
const fetchHospitalDept = async () => {
  const response = await axios.get('https://pay1one.com/api/get/203/hospital-dept');
  if (!response.data.success) {
    throw new Error('Failed to fetch hospital department');
  }
  return response.data.data.orgDepts;
};

const fetchHospitalRecords = async (deptId) => {
  if (!deptId) return [];
  const response = await axios.get(`https://pay1one.com/api/get/203/${deptId}/hospital-revenuehead`);
  if (!response.data.success) {
    throw new Error('Failed to fetch hospital records');
  }
  return response.data.data;
};

const fetchAllHospitalRecords = async () => {
  const cached = localStorage.getItem('allHospitalRecords');
  if (cached) {
    return JSON.parse(cached);
  }
  const response = await axios.get('https://pay1one.com/api/get/203/hospitalRecords');
  if (!response.data.success) {
    throw new Error('Failed to fetch all hospital records');
  }
  const data = response.data.data.orgRevenueHeads;
  localStorage.setItem('allHospitalRecords', JSON.stringify(data));
  return data;
};

// API functions for invoices
const fetchStatistics = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }
  try {
    const response = await axios.get('https://api.uchselfservice.net/api/invoices/invoices/statistics/', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.data) {
      throw new Error('Failed to fetch statistics');
    }
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Session expired. Please log in again.');
    }
    throw error;
  }
};

const fetchInvoices = async (params = {}) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }
  const queryParams = new URLSearchParams();
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.status) queryParams.append('status', params.status);
  if (params.method) queryParams.append('method', params.method);
  const url = `https://api.uchselfservice.net/api/invoices/invoices/filter/?${queryParams.toString()}`;
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.data) {
      throw new Error('Failed to fetch invoices');
    }
    return response.data.results || [];
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Session expired. Please log in again.');
    }
    throw error;
  }
};

const AgentPanel = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: '',
    method: '',
    department: '',
  });

  // Restrict access to agents
  if (user.role !== 'AGENT') {
    return <div className="min-h-screen bg-gray-50 text-[#2F2D2E] p-6">Access Denied: Agent privileges required.</div>;
  }

  // Fetch departments
  const { data: departments = [], isLoading: deptLoading, error: deptError } = useQuery({
    queryKey: ['hospitalDepartment'],
    queryFn: fetchHospitalDept,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch revenue heads
  const { data: revenueHeads = [], isLoading: revenueLoading, error: revenueError } = useQuery({
    queryKey: ['hospitalRecords', filters.department],
    queryFn: async () => {
      if (filters.department === 'all') {
        return fetchAllHospitalRecords();
      } else if (filters.department) {
        return fetchHospitalRecords(filters.department);
      } else {
        return [];
      }
    },
    enabled: !!filters.department,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch statistics
  const { data: stats = {}, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['invoicesStatistics'],
    queryFn: fetchStatistics,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch invoices
  const { data: invoices = [], isLoading: invoicesLoading, error: invoicesError } = useQuery({
    queryKey: ['invoices', filters],
    queryFn: () => fetchInvoices({
      start_date: filters.dateFrom,
      end_date: filters.dateTo,
      status: filters.status,
      method: filters.method,
    }),
    enabled: !!filters.dateFrom || !!filters.dateTo || !!filters.status || !!filters.method,
    staleTime: 5 * 60 * 1000,
  });

  // Map revenue heads to a lookup for department names
  const revenueHeadMap = useMemo(() => {
    return revenueHeads.reduce((acc, head) => {
      acc[head.name] = head.department?.name || 'Unknown';
      return acc;
    }, {});
  }, [revenueHeads]);

  // Process invoices for display
  const transactions = useMemo(() => {
    return invoices.flatMap((invoice) => {
      return invoice.items.map((item, index) => ({
        id: `${invoice.id}-${index}`,
        invoiceId: invoice.id,
        reference: invoice.reference,
        date: invoice.created_at.split('T')[0],
        amount: parseFloat(item.amount),
        revenueHead: item.service.name,
        department: item.service.department ? item.service.department.name : revenueHeadMap[item.service.name] || 'Unknown',
        description: item.service.description || `Payment for ${item.service.name}`,
        status: invoice.status,
        paymentMethod: invoice.payment_method,
        customerName: invoice.customer_meta ? invoice.customer_meta.name : 'Unknown',
        customerEmail: invoice.customer_email,
        paidAt: invoice.paid_at ? invoice.paid_at.split('T')[0] : null,
      }));
    });
  }, [invoices, revenueHeadMap]);

  // Filter transactions (client-side filtering for department)
  const filteredTransactions = useMemo(() => {
    return transactions.filter((tx) => {
      const txDate = new Date(tx.date);
      const fromDate = new Date(filters.dateFrom);
      const toDate = new Date(filters.dateTo + 'T23:59:59');
      return (
        (!filters.dateFrom || txDate >= fromDate) &&
        (!filters.dateTo || txDate <= toDate) &&
        (!filters.status || tx.status === filters.status) &&
        (!filters.method || tx.paymentMethod === filters.method) &&
        (!filters.department || filters.department === 'all' || tx.department === filters.department)
      );
    });
  }, [transactions, filters]);

  // Set default date range
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    setFilters((prev) => ({
      ...prev,
      dateFrom: lastMonth.toISOString().split('T')[0],
      dateTo: today,
    }));
  }, []);

  // Summary calculations
  const totalCount = filteredTransactions.length;
  const totalRevenue = filteredTransactions.reduce((sum, tx) => sum + tx.amount, 0);
  const avgTransaction = totalCount > 0 ? (totalRevenue / totalCount).toFixed(2) : 0;
  const recentCount = filteredTransactions.filter((tx) => {
    const txDate = new Date(tx.date);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return txDate >= weekAgo;
  }).length;

  // Chart data (by payment method)
  const methodData = stats.method_counts || {};
  const chartData = {
    labels: Object.keys(methodData),
    datasets: [
      {
        label: 'Revenue by Payment Method',
        data: Object.values(methodData).map((m) => m.sum_amount),
        backgroundColor: ['#ff6384', '#36a2eb', '#ffcd56', '#4bc0c0', '#9966ff'],
      },
    ],
  };
  const chartOptions = {
    scales: { y: { beginAtZero: true } },
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Export to CSV
  const exportTransactions = () => {
    if (filteredTransactions.length === 0) {
      alert('No transactions to export.');
      return;
    }
    let csv = 'ID,Invoice ID,Reference,Date,Amount,Revenue Head,Department,Description,Status,Payment Method,Customer Name,Customer Email,Paid At\n';
    filteredTransactions.forEach((tx) => {
      csv += `"${tx.id}","${tx.invoiceId}","${tx.reference}","${tx.date}","₦${tx.amount.toFixed(2)}","${tx.revenueHead}","${tx.department}","${tx.description}","${tx.status}","${tx.paymentMethod}","${tx.customerName}","${tx.customerEmail}","${tx.paidAt}"\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transactions.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-[#2F2D2E]">
      <header className="bg-[#2F2D2E] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-[#009D50] rounded-full flex items-center justify-center text-white font-bold text-sm">
              AG
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-white">Agent Transactions Dashboard</h1>
          </div>
          <div className="text-xs sm:text-sm text-center">
            <p className="font-medium text-white">{user.first_name} {user.last_name}</p>
            <p className="text-gray-300">Agent Panel</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="bg-[#2F2D2E] p-4 sm:p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 text-white">Summary</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-gray-700 p-3 sm:p-4 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-300 font-medium">Total Transactions</p>
              <p className="text-lg sm:text-xl font-semibold text-white">{totalCount}</p>
            </div>
            <div className="bg-gray-700 p-3 sm:p-4 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-300 font-medium">Total Revenue</p>
              <p className="text-lg sm:text-xl font-semibold text-white">₦{totalRevenue.toLocaleString()}</p>
            </div>
            <div className="bg-gray-700 p-3 sm:p-4 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-300 font-medium">Average Transaction</p>
              <p className="text-lg sm:text-xl font-semibold text-white">₦{avgTransaction}</p>
            </div>
            <div className="bg-gray-700 p-3 sm:p-4 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-300 font-medium">Recent Transactions</p>
              <p className="text-lg sm:text-xl font-semibold text-white">{recentCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-[#2F2D2E] p-4 sm:p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 text-white">Filters</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="flex flex-col w-full">
              <label className="text-xs sm:text-sm text-gray-300 mb-1">Date From</label>
              <input
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009D50] text-xs sm:text-sm"
              />
            </div>
            <div className="flex flex-col w-full">
              <label className="text-xs sm:text-sm text-gray-300 mb-1">Date To</label>
              <input
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009D50] text-xs sm:text-sm"
              />
            </div>
            <div className="flex flex-col w-full">
              <label className="text-xs sm:text-sm text-gray-300 mb-1">Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009D50] text-xs sm:text-sm"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
            <div className="flex flex-col w-full">
              <label className="text-xs sm:text-sm text-gray-300 mb-1">Payment Method</label>
              <select
                name="method"
                value={filters.method}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009D50] text-xs sm:text-sm"
              >
                <option value="">All Methods</option>
                <option value="flutterwave">Flutterwave</option>
                <option value="monnify">Monnify</option>
                <option value="remita">Remita</option>
                <option value="wallet">Wallet</option>
              </select>
            </div>
            <div className="flex flex-col w-full">
              <label className="text-xs sm:text-sm text-gray-300 mb-1">Department</label>
              <select
                name="department"
                value={filters.department}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009D50] text-xs sm:text-sm"
              >
                <option value="">All Departments</option>
                <option value="all">All Departments (Combined)</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end w-full">
              <Button
                onClick={exportTransactions}
                className="w-full bg-[#009D50] hover:bg-[#007A3E] text-white rounded-lg h-9 sm:h-10 text-xs sm:text-sm font-semibold"
              >
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-[#2F2D2E] p-4 sm:p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 text-white">Revenue by Payment Method</h2>
          <div className="h-64">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>

        <div className="bg-[#2F2D2E] p-4 sm:p-6 rounded-lg shadow-md">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 text-white">Transactions</h2>
          {(statsLoading || invoicesLoading || deptLoading || revenueLoading) ? (
            <p className="text-gray-300 text-sm">Loading transactions...</p>
          ) : (statsError || invoicesError || deptError || revenueError) ? (
            <p className="text-red-400 text-sm">Error: {(statsError || invoicesError || deptError || revenueError)?.message}</p>
          ) : filteredTransactions.length === 0 ? (
            <p className="text-gray-300 text-sm">No transactions found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-white">
                <thead>
                  <tr className="bg-gray-700">
                    <th className="p-3">ID</th>
                    <th className="p-3">Reference</th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Amount</th>
                    <th className="p-3">Revenue Head</th>
                    <th className="p-3">Department</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Payment Method</th>
                    <th className="p-3">Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-gray-600">
                      <td className="p-3">{tx.invoiceId}</td>
                      <td className="p-3">{tx.reference}</td>
                      <td className="p-3">{tx.date}</td>
                      <td className="p-3">₦{tx.amount.toLocaleString()}</td>
                      <td className="p-3">{tx.revenueHead}</td>
                      <td className="p-3">{tx.department}</td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            tx.status === 'completed'
                              ? 'bg-green-600'
                              : tx.status === 'pending'
                              ? 'bg-yellow-600'
                              : tx.status === 'failed'
                              ? 'bg-red-600'
                              : 'bg-gray-600'
                          }`}
                        >
                          {tx.status}
                        </span>
                      </td>
                      <td className="p-3">{tx.paymentMethod}</td>
                      <td className="p-3">
                        {tx.customerName} ({tx.customerEmail})
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AgentPanel;